<div class="page-shell">
  <div class="page-header">
    <div>
      <h1>Compare feature flag environments</h1>
      <p>Read-only comparison of Managed Feature Flags between the connected QA and Production environments.</p>
    </div>
    <button mat-stroked-button type="button" (click)="backToFlags()">
      <mat-icon>arrow_back</mat-icon>
      Back to flags
    </button>
  </div>

  <section class="surface-panel" *ngIf="state === 'loading'">
    <div class="loading-state"><mat-spinner diameter="28"></mat-spinner><span>Loading environment comparison…</span></div>
  </section>

  <section class="surface-panel state-panel" *ngIf="state === 'unsupported'">
    <strong>Environment comparison is available only when connected to QA or Production.</strong>
  </section>

  <section class="surface-panel state-panel error-state" *ngIf="state === 'unavailable'">
    <div>
      <strong>Environment comparison is temporarily unavailable.</strong>
      <p *ngIf="unavailableEnvironments.length">Unavailable: {{ unavailableEnvironmentLabels() }}.</p>
      <p *ngIf="!unavailableEnvironments.length">One or more comparison environments are unavailable.</p>
    </div>
    <button mat-stroked-button type="button" (click)="loadComparison()">Retry</button>
  </section>

  <section class="surface-panel state-panel error-state" *ngIf="state === 'authorization'">
    <strong>You are not authorized to compare Managed Feature Flag environments.</strong>
  </section>

  <section class="surface-panel state-panel error-state" *ngIf="state === 'error'">
    <div><strong>Environment comparison is unavailable.</strong><p>Try again after checking the connected backend.</p></div>
    <button mat-stroked-button type="button" (click)="loadComparison()">Retry</button>
  </section>

  <ng-container *ngIf="state === 'success' && comparison">
    <p class="environment-note">Comparison returned by {{ environmentLabel(comparison.environments.current) }} for {{ environmentLabel(comparison.environments.qa) }} and {{ environmentLabel(comparison.environments.production) }}.</p>
    <section class="summary-grid" aria-label="Comparison summary">
      <div class="summary-card"><span>Total</span><strong>{{ comparison.summary.total }}</strong></div>
      <div class="summary-card"><span>Same</span><strong>{{ comparison.summary.same }}</strong></div>
      <div class="summary-card"><span>Changed</span><strong>{{ comparison.summary.changed }}</strong></div>
      <div class="summary-card"><span>Only in {{ environmentLabel(comparison.environments.qa) }}</span><strong>{{ comparison.summary.onlyInQa }}</strong></div>
      <div class="summary-card"><span>Only in {{ environmentLabel(comparison.environments.production) }}</span><strong>{{ comparison.summary.onlyInProduction }}</strong></div>
    </section>

    <section class="surface-panel" *ngIf="comparison.results.length === 0">
      <div class="empty-state"><strong>No Managed Feature Flags were returned for comparison.</strong></div>
    </section>

    <section class="results-list" *ngIf="comparison.results.length > 0">
      <article class="surface-panel comparison-result" *ngFor="let result of comparison.results; trackBy: trackByKey">
        <div class="result-header">
          <div><h2>{{ result.key }}</h2><span class="status-pill" [ngClass]="'status-' + result.status">{{ statusLabel(result.status) }}</span></div>
          <button mat-stroked-button type="button" (click)="toggleExpanded(result)" [attr.aria-expanded]="isExpanded(result)">
            <mat-icon>{{ isExpanded(result) ? 'expand_less' : 'expand_more' }}</mat-icon>
            {{ isExpanded(result) ? 'Hide details' : 'View details' }}
          </button>
        </div>
        <p class="difference-summary" *ngIf="result.differentFields.length">Different fields: {{ differentFieldLabels(result) }}</p>
        <p class="difference-summary" *ngIf="!result.differentFields.length">No differing fields reported by the backend.</p>

        <div class="snapshot-columns" *ngIf="isExpanded(result)">
          <section class="snapshot-column">
            <h3>{{ environmentLabel(comparison.environments.qa) }}</h3>
            <p class="not-present" *ngIf="!hasSnapshot(result.qa)">Not present</p>
            <ng-container *ngIf="hasSnapshot(result.qa)">
              <dl>
                <dt>Name</dt><dd>{{ textValue(result.qa?.name, result.qa) }}</dd>
                <dt>Description</dt><dd>{{ textValue(result.qa?.description, result.qa) }}</dd>
                <dt>App targets</dt><dd>{{ appTargets(result.qa) }}</dd>
                <dt>Active</dt><dd>{{ booleanValue(result.qa?.isActive === undefined ? null : result.qa?.isActive) }}</dd>
                <dt>Default enabled</dt><dd>{{ booleanValue(result.qa?.defaultEnabled === undefined ? null : result.qa?.defaultEnabled) }}</dd>
                <dt>Included users</dt><dd>{{ values(result.qa?.userOverrides?.includeUserIds, result.qa) }}</dd>
                <dt>Excluded users</dt><dd>{{ values(result.qa?.userOverrides?.excludeUserIds, result.qa) }}</dd>
                <dt>Schema version</dt><dd>{{ result.qa?.schemaVersion }}</dd>
              </dl>
              <div class="rules"><h4>Targeting rules</h4><p *ngIf="!rules(result.qa).length">None</p><div class="rule" *ngFor="let rule of rules(result.qa); let index = index"><strong>Rule {{ index + 1 }}: {{ rule.effect === 'enable' ? 'Enable' : 'Disable' }}</strong><dl><dt>Include teams</dt><dd>{{ values(rule.teamIds, result.qa) }}</dd><dt>Exclude teams</dt><dd>{{ values(rule.excludeTeamIds, result.qa) }}</dd><dt>Include companies</dt><dd>{{ values(rule.companyIds, result.qa) }}</dd><dt>Exclude companies</dt><dd>{{ values(rule.excludeCompanyIds, result.qa) }}</dd><dt>Include partners</dt><dd>{{ values(rule.partnerIds, result.qa) }}</dd><dt>Exclude partners</dt><dd>{{ values(rule.excludePartnerIds, result.qa) }}</dd><dt>Include states</dt><dd>{{ values(rule.states, result.qa) }}</dd><dt>Exclude states</dt><dd>{{ values(rule.excludeStates, result.qa) }}</dd></dl></div></div>
            </ng-container>
          </section>
          <section class="snapshot-column">
            <h3>{{ environmentLabel(comparison.environments.production) }}</h3>
            <p class="not-present" *ngIf="!hasSnapshot(result.production)">Not present</p>
            <ng-container *ngIf="hasSnapshot(result.production)">
              <dl>
                <dt>Name</dt><dd>{{ textValue(result.production?.name, result.production) }}</dd>
                <dt>Description</dt><dd>{{ textValue(result.production?.description, result.production) }}</dd>
                <dt>App targets</dt><dd>{{ appTargets(result.production) }}</dd>
                <dt>Active</dt><dd>{{ booleanValue(result.production?.isActive === undefined ? null : result.production?.isActive) }}</dd>
                <dt>Default enabled</dt><dd>{{ booleanValue(result.production?.defaultEnabled === undefined ? null : result.production?.defaultEnabled) }}</dd>
                <dt>Included users</dt><dd>{{ values(result.production?.userOverrides?.includeUserIds, result.production) }}</dd>
                <dt>Excluded users</dt><dd>{{ values(result.production?.userOverrides?.excludeUserIds, result.production) }}</dd>
                <dt>Schema version</dt><dd>{{ result.production?.schemaVersion }}</dd>
              </dl>
              <div class="rules"><h4>Targeting rules</h4><p *ngIf="!rules(result.production).length">None</p><div class="rule" *ngFor="let rule of rules(result.production); let index = index"><strong>Rule {{ index + 1 }}: {{ rule.effect === 'enable' ? 'Enable' : 'Disable' }}</strong><dl><dt>Include teams</dt><dd>{{ values(rule.teamIds, result.production) }}</dd><dt>Exclude teams</dt><dd>{{ values(rule.excludeTeamIds, result.production) }}</dd><dt>Include companies</dt><dd>{{ values(rule.companyIds, result.production) }}</dd><dt>Exclude companies</dt><dd>{{ values(rule.excludeCompanyIds, result.production) }}</dd><dt>Include partners</dt><dd>{{ values(rule.partnerIds, result.production) }}</dd><dt>Exclude partners</dt><dd>{{ values(rule.excludePartnerIds, result.production) }}</dd><dt>Include states</dt><dd>{{ values(rule.states, result.production) }}</dd><dt>Exclude states</dt><dd>{{ values(rule.excludeStates, result.production) }}</dd></dl></div></div>
            </ng-container>
          </section>
        </div>
      </article>
    </section>
  </ng-container>
</div>
